import React, { useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { ScrollToTop } from '../components/ScrollToTop';
import { places, getRelatedPlaces } from '../data/places';
export function PlaceDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const place = places.find(p => p.id === id);
  const relatedPlaces = place ? getRelatedPlaces(place.id) : [];
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);
  if (!place) {
    return <Navigate to="/" replace data-id="element-1304" />;
  }
  return <div className="min-h-screen bg-white" data-id="element-1305">
      {/* Hero Section - Text Only */}
      <div className="relative bg-gray-900 overflow-hidden" data-id="element-1306">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" data-id="element-1307"></div>

        <Link to="/" className="absolute top-8 left-8 bg-white/10 backdrop-blur-sm text-white px-6 py-3 hover:bg-white/20 transition-colors flex items-center gap-2 text-sm font-light uppercase tracking-widest border border-white/20 z-10" data-id="element-1308">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} data-id="element-1309" />
          Discover
        </Link>

        <div className="relative max-w-7xl mx-auto px-8 lg:px-12 py-32 md:py-48" data-id="element-1310">
          <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.2,
          duration: 0.8
        }} data-id="element-1311">
            <div className="h-px w-16 bg-amber-500 mb-8" data-id="element-1312"></div>
            <div className="inline-block px-5 py-2 border border-white/30 text-white text-xs font-light uppercase tracking-[0.3em] mb-8" data-id="element-1313">
              {place.category}
            </div>
            <h1 className="text-6xl md:text-8xl font-light text-white mb-8 leading-[1.1] tracking-tight max-w-4xl" data-id="element-1314">
              {place.name}
            </h1>
            <div className="flex items-center gap-3 text-white/80" data-id="element-1315">
              <MapPin className="w-5 h-5" strokeWidth={1.5} data-id="element-1316" />
              <span className="text-lg font-light tracking-wide" data-id="element-1317">
                {place.location}
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 lg:px-12 py-20" data-id="element-1318">
        <div className="max-w-4xl space-y-16" data-id="element-1319">
          {/* About Section */}
          <section data-id="element-1320">
            <div className="h-px w-16 bg-amber-500 mb-8" data-id="element-1321"></div>
            <h2 className="text-4xl font-light text-gray-900 mb-8 tracking-tight" data-id="element-1322">
              About
            </h2>
            <div className="space-y-6 text-gray-700 text-lg leading-relaxed font-light" data-id="element-1323">
              <p data-id="element-1324">{place.description}</p>

              <p data-id="element-1325">
                This destination represents one of Nairobi's most distinctive
                experiences, offering visitors a unique glimpse into the city's
                character. Whether you're a first-time visitor or a returning
                traveler, you'll find something new to discover with each visit.
              </p>

              <p data-id="element-1326">
                The location has become an integral part of Nairobi's cultural
                landscape, attracting both locals and international visitors who
                seek authentic experiences. Its significance extends beyond mere
                tourism, serving as a gathering place that reflects the city's
                diverse community and vibrant spirit.
              </p>

              <p data-id="element-1327">
                Throughout the year, the atmosphere here evolves with the
                seasons and local events, creating a dynamic environment that
                never feels quite the same twice. This ever-changing nature adds
                to its appeal, making it a place worth revisiting to fully
                appreciate all it has to offer.
              </p>
            </div>
          </section>

          {/* Highlights */}
          <section data-id="element-1328">
            <div className="h-px w-16 bg-amber-500 mb-8" data-id="element-1329"></div>
            <h2 className="text-4xl font-light text-gray-900 mb-8 tracking-tight" data-id="element-1330">
              Highlights
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-1331">
              {place.highlights.map((highlight, index) => <motion.div key={index} className="flex items-start gap-4 p-6 bg-gray-50 border border-gray-100" initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.1 * index
            }} data-id="element-1332">
                  <div className="w-1 h-1 bg-amber-500 rounded-full mt-2 flex-shrink-0" data-id="element-1333" />
                  <p className="text-gray-700 font-light leading-relaxed" data-id="element-1334">
                    {highlight}
                  </p>
                </motion.div>)}
            </div>
          </section>

          {/* Categories */}
          <section data-id="element-1335">
            <div className="h-px w-16 bg-amber-500 mb-8" data-id="element-1336"></div>
            <h2 className="text-4xl font-light text-gray-900 mb-8 tracking-tight" data-id="element-1337">
              Categories
            </h2>
            <div className="flex flex-wrap gap-4" data-id="element-1338">
              {place.categories.map(category => <span key={category} className="px-6 py-3 border border-gray-900 text-gray-900 text-sm font-light tracking-wider" data-id="element-1339">
                  {category}
                </span>)}
            </div>
          </section>
        </div>

        {/* Related Places */}
        {relatedPlaces.length > 0 && <section className="mt-32" data-id="element-1340">
            <div className="h-px w-16 bg-amber-500 mb-8" data-id="element-1341"></div>
            <h2 className="text-4xl font-light text-gray-900 mb-12 tracking-tight" data-id="element-1342">
              Similar Destinations
            </h2>
            <div className="grid md:grid-cols-3 gap-10" data-id="element-1343">
              {relatedPlaces.map(relatedPlace => <Link key={relatedPlace.id} to={`/place/${relatedPlace.id}`} className="group block" data-id="element-1344">
                  <div className="border border-gray-200 hover:border-gray-300 transition-all overflow-hidden h-full flex flex-col" data-id="element-1345">
                    <div className="p-8 flex-1 flex flex-col" data-id="element-1346">
                      <span className="text-xs text-gray-500 uppercase tracking-widest mb-4 font-light" data-id="element-1347">
                        {relatedPlace.category}
                      </span>
                      <h3 className="text-2xl font-light text-gray-900 mb-4 group-hover:text-gray-600 transition-colors" data-id="element-1348">
                        {relatedPlace.name}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-3 font-light leading-relaxed" data-id="element-1349">
                        {relatedPlace.description}
                      </p>
                    </div>
                  </div>
                </Link>)}
            </div>
          </section>}
      </div>

      <ScrollToTop data-id="element-1350" />
      <Footer data-id="element-1351" />
    </div>;
}